import { Component, OnDestroy } from '@angular/core';
import { AlertController, Platform } from '@ionic/angular/standalone';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@capacitor/status-bar';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { RouterModule } from '@angular/router';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { ConnectionStatusComponent } from './components/connection-status/connection-status.component';
import { PreloadService } from './services/preload.service';
import { ModulePreloadService } from './services/module-preload.service';
import { LazyGlitchTipService } from './services/lazy-glitchtip.service';
import { AuthenticationService } from './services/auth.service';
import { AppStorageService } from './services/app-storage.service';
import { Subscription, interval } from 'rxjs';
import { setAnalyticsUser } from './shared/analytics';
import { UploadCheckpointService } from './services/upload-checkpoint.service';

@Component({
  selector: 'app-root',
  template: `<ion-app>
    <ion-router-outlet></ion-router-outlet>
    <app-connection-status></app-connection-status>
  </ion-app>`,
  standalone: true,
  imports: [RouterModule, IonApp, IonRouterOutlet, ConnectionStatusComponent],
})
export class AppComponent implements OnDestroy {
  private static readonly UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
  private static readonly UPDATE_IGNORE_DURATION_MS = 10 * 60 * 1000; // 10 minutes

  private ignoreUpdate = false;
  private subscriptions = new Subscription();

  constructor(
    private platform: Platform,
    private alertController: AlertController,
    private swUpdate: SwUpdate,
    private translate: TranslateService,
    private preloadService: PreloadService,
    private modulePreloadService: ModulePreloadService,
    private glitchTipService: LazyGlitchTipService,
    private authService: AuthenticationService,
    private appStorageService: AppStorageService,
    private uploadCheckpointService: UploadCheckpointService,
  ) {
    this.initializeApp();
  }

  async initializeApp() {
    this.uploadCheckpointService.reportInterruptedUploads();

    if (this.platform.is('capacitor')) {
      await StatusBar.show();
      await SplashScreen.hide();
    }

    // Set the default language
    this.translate.setDefaultLang('he');

    // Use the browser's language if available, otherwise use the default language
    const browserLang = this.translate.getBrowserLang();
    this.translate.use(browserLang?.match(/he|uk/) ? browserLang : 'he');

    // Check if there's a language saved in storage
    try {
      const savedLang = await this.appStorageService.getItem<string>('selectedLanguage');
      if (savedLang) {
        this.translate.use(savedLang);
      }
    } catch (error) {
      console.error('Error loading saved language preference:', error);
    }

    await this.setupServiceWorker();

    // Clean up old translation cache keys (one-time migration to Service Worker caching)
    try {
      const migrationComplete = await this.appStorageService.getItem<string>('i18n-sw-migration-v1');
      if (!migrationComplete) {
        await this.appStorageService.removeItemsWithPattern('translate-cache');
        await this.appStorageService.setItem('i18n-sw-migration-v1', 'complete');
      }
    } catch (error) {
      console.error('Error clearing old translation cache:', error);
    }

    // Setup auth listener to trigger preloading when user authenticates
    this.setupAuthListener();
  }

  /**
   * Setup authentication listener to trigger API preloading after login
   */
  private setupAuthListener(): void {
    this.subscriptions.add(
      this.authService.user$
        .pipe(
          // Only trigger when user is logged in with verified email
          filter((user) => !!user?.emailVerified),
        )
        .subscribe((user) => {
          this.preloadService.initPreload();
          this.modulePreloadService.startPreloading();
          this.glitchTipService.setUserContext({ id: user.uid, email: user.email });
          setAnalyticsUser({ uid: user.uid });
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private async setupServiceWorker() {
    if (this.swUpdate.isEnabled) {
      try {
        await this.swUpdate.checkForUpdate();

        this.subscriptions.add(
          interval(AppComponent.UPDATE_CHECK_INTERVAL_MS).subscribe(() => {
            this.swUpdate.checkForUpdate().catch((err) => {
              console.error('Error checking for updates:', err);
            });
          }),
        );

        this.swUpdate.versionUpdates
          .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
          .subscribe(async (event) => {
            if (!this.ignoreUpdate) {
              const alert = await this.alertController.create({
                header: await firstValueFrom(this.translate.get('NEW_VERSION_AVAILABLE')),
                message: await firstValueFrom(this.translate.get('UPDATE_MESSAGE')),
                buttons: [
                  {
                    text: await firstValueFrom(this.translate.get('COMMON.IGNORE')),
                    role: 'cancel',
                    handler: () => {
                      this.ignoreUpdate = true;
                      setTimeout(() => {
                        this.ignoreUpdate = false;
                      }, AppComponent.UPDATE_IGNORE_DURATION_MS);
                    },
                  },
                  {
                    text: await firstValueFrom(this.translate.get('UPDATE_NOW')),
                    handler: async () => {
                      await this.performServiceWorkerUpdate();
                    },
                  },
                ],
                backdropDismiss: false,
              });
              await alert.present();
            }
          });

        this.swUpdate.unrecoverable.subscribe(async (event) => {
          console.error('SW Update Error (unrecoverable):', event.reason);

          this.glitchTipService.captureException(new Error('Service Worker Unrecoverable'), {
            tags: {
              error_source: 'service_worker',
              error_type: 'sw_unrecoverable',
            },
            level: 'error',
            extra: {
              reason: event.reason,
            },
          });

          const alert = await this.alertController.create({
            header: await firstValueFrom(this.translate.get('ERROR')),
            message: await firstValueFrom(this.translate.get('SW_UPDATE_ERROR')),
            buttons: [
              {
                text: 'OK',
                handler: async () => {
                  await this.performServiceWorkerUpdate(true); // Force nuclear option
                },
              },
            ],
          });
          await alert.present();
        });
      } catch (err) {
        console.error('Service Worker setup failed:', err);

        this.glitchTipService.captureException(err instanceof Error ? err : new Error(String(err)), {
          tags: {
            error_source: 'service_worker',
            error_type: 'setup_failed',
          },
          level: 'error',
        });
      }
    }
  }

  /**
   * Perform Service Worker update with cache clearing
   * @param forceNuclear - If true, unregister all service workers (nuclear option for unrecoverable errors)
   */
  private async performServiceWorkerUpdate(forceNuclear = false): Promise<void> {
    try {
      if ('caches' in window) {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((key) => caches.delete(key)));
      }

      try {
        await this.appStorageService.removeItemsWithPattern('translate-cache');
      } catch (error) {
        console.error('Error clearing i18n cache:', error);
      }

      if (forceNuclear) {
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((reg) => reg.unregister()));
        }
      } else {
        await this.swUpdate.activateUpdate();
      }

      window.location.reload();
    } catch (err) {
      console.error('Failed to perform SW update:', err);
      window.location.reload();
    }
  }
}
